import React, { ReactNode, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UIContext } from "../providers/UIProvider";
import { EditBonusApplicationPayload } from "../services/bonus/types";
import { useCreateBonusApplication, useEditBonusApplication } from "../hooks/useBonusQueries";

export const useActionButtons = () => {
  const navigate = useNavigate();
  const [state, dispatch]: any = useContext(UIContext);

  const createBonusMutation = useCreateBonusApplication();
  const editBonusMutation = useEditBonusApplication();

  const [isConfirmSubmissionDialogOpen, setIsConfirmSubmissionDialogOpen] =
    useState<boolean>(false);
  const [isConfirmOverrideDialogOpen, setIsConfirmOverrideDialogOpen] =
    useState<boolean>(false);
  const [overrideConfirmName, setOverrideConfirmName] = useState<string>("");
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [fullName, setFullName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<ReactNode>(null);

  const handleCreate = (
    projectNumbers: string | null,
    bonusSheetJson: string,
    userAction: "Draft" | "Submitted",
    amount: number | null = null,
    applicationType: string | null = null,
    hasOverriddenHierarchy: boolean = false,
    bonusRecipientEmail: string | null = null,
    submitterEmail: string | null = null,
    submitterName: string | null = null,
    vpEmail: string | null = null,
    comments: string[] = [],
    isConfirmed: boolean = false,
    formValidationErrors: string[] = [],
    isOverrideConfirmed: boolean = false
  ) => {
    console.log("Creating bonus application with data:", {
      projectNumbers,
      bonusSheetJson,
      userAction,
      amount,
      applicationType,
      hasOverriddenHierarchy,
      bonusRecipientEmail,
      submitterEmail,
      submitterName,
      vpEmail,
    });

    // Clear any existing error message
    setErrorMessage(null);

    // ✅ Numbered list validation errors (forces numbering even if CSS resets remove list styles)
    if (userAction === "Submitted" && formValidationErrors.length > 0) {
      const count = formValidationErrors.length;

      const errorMsg = (
        <div>
          <div>
            {count} required {count === 1 ? "field is" : "fields are"} incomplete:
          </div>

          <ol
            style={{
              marginTop: 8,
              marginBottom: 0,
              paddingLeft: 24,
              listStyleType: "decimal",
            }}
          >
            {formValidationErrors.map((error, index) => (
              <li key={`${index}-${error}`}>{error}</li>
            ))}
          </ol>
        </div>
      );

      setErrorMessage(errorMsg);

      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: {
          type: "error",
          message: errorMsg,
        },
      });

      return;
    }

    if (
      (userAction === "Submitted" || userAction === "Draft") &&
      (!projectNumbers &&
        (applicationType === "CM3" ||
          applicationType === "PM" ||
          applicationType === "SPM"))
    ) {
      const errorMsg = "Project numbers are required for submission";
      setErrorMessage(errorMsg);
      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: {
          type: "error",
          message: errorMsg,
        },
      });
      return;
    }

    if (!(userAction === "Draft" || userAction === "Submitted")) {
      const errorMsg = "Invalid user action. Must be 'Draft' or 'Submitted'";
      setErrorMessage(errorMsg);
      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: {
          type: "error",
          message: errorMsg,
        },
      });
      return;
    }

    if (userAction === "Submitted" && hasOverriddenHierarchy && !isOverrideConfirmed) {
      setOverrideConfirmName("");
      setPendingAction(() => () =>
        handleCreate(
          projectNumbers,
          bonusSheetJson,
          userAction,
          amount,
          applicationType,
          hasOverriddenHierarchy,
          bonusRecipientEmail,
          submitterEmail,
          submitterName,
          vpEmail,
          comments,
          true,
          formValidationErrors,
          true
        )
      );
      setIsConfirmOverrideDialogOpen(true);
      return;
    }

    if (userAction === "Submitted" && !isConfirmed) {
      setIsConfirmSubmissionDialogOpen(true);
      return;
    }

    const applicationData = {
      UserAction: userAction,
      projectNumbers: projectNumbers,
      Amount: amount,
      BonusSheet: bonusSheetJson,

      BonusRecipientEmail: bonusRecipientEmail,
      // SubmitterEmail: submitterEmail,
      // VPEmail: vpEmail,
      VPReviewedBy: vpEmail,
      ApplicationType: applicationType,
      HasOverriddenHierarchy: hasOverriddenHierarchy,
      Comments: comments,
    };

    console.log("Creating bonus application with data:", applicationData);

    // @ts-ignore
    createBonusMutation.mutate(applicationData, {
      onSuccess: () => {
        setErrorMessage(null); // Clear any error message on success
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "success",
            message: `Form ${userAction === "Draft" ? "Saved as draft" : "Submitted"
              } successfully`,
          },
        });
        navigate("/review-items");
      },
      onError: (error: any) => {
        const errorMsg = "Error submitting form. Please try again.";
        setErrorMessage(errorMsg);
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "error",
            message: errorMsg,
          },
        });
        console.error("Error submitting form:", error);
      },
    });
  };

  const handleApprove = (
    id: number,
    comments: string[] = [],
    hasOverriddenHierarchy: boolean = false,
    confirmingName: string | null = null,
    approverName: string | null = null,
    isOverrideConfirmed: boolean = false
  ) => {
    if (!id) {
      console.error("Missing required IDs for approval");
      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: {
          type: "error",
          message: "Missing required information for approval",
        },
      });
      return;
    }

    if (hasOverriddenHierarchy && !isOverrideConfirmed) {
      setOverrideConfirmName("");
      setPendingAction(() => () =>
        handleApprove(id, comments, hasOverriddenHierarchy, confirmingName, approverName, true)
      );
      setIsConfirmOverrideDialogOpen(true);
      return;
    }

    const editData: EditBonusApplicationPayload = {
      BonusApplicationId: id,
      UserAction: "Approved",
      Amount: null,
      BonusSheet: null,
      ExEmployee: null,
      Comments: comments,
    };

    // @ts-ignore
    editBonusMutation.mutate(editData, {
      onSuccess: () => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "success",
            message: "Bonus application approved successfully",
          },
        });
        navigate("/review-items");
      },
      onError: (error: any) => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "error",
            message: "Error approving bonus application",
          },
        });
        console.error("Error approving bonus application:", error);
      },
    });
  };

  const handleReject = (
    id: number,
    comments: string[] = [],
    hasOverriddenHierarchy: boolean = false,
    approverName: string | null = null,
    isOverrideConfirmed: boolean = false
  ) => {
    if (!id) return;

    setErrorMessage(null);

    if (comments.length === 0) {
      const errorMsg = "Comments are required for rejection.";
      console.error(errorMsg);
      setErrorMessage(errorMsg);
      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: {
          type: "error",
          message: "Comments are required for rejection",
        },
      });
      return;
    }

    if (hasOverriddenHierarchy && !isOverrideConfirmed) {
      setOverrideConfirmName("");
      setPendingAction(() => () =>
        handleReject(id, comments, hasOverriddenHierarchy, approverName, true)
      );
      setIsConfirmOverrideDialogOpen(true);
      return;
    }

    const editData: EditBonusApplicationPayload = {
      BonusApplicationId: id,
      UserAction: "Rejected",
      Amount: null,
      BonusSheet: null,
      ExEmployee: null,
      Comments: comments,
    };

    // @ts-ignore
    editBonusMutation.mutate(editData, {
      onSuccess: () => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "success",
            message: "Bonus application rejected successfully",
          },
        });
        navigate("/review-items");
      },
      onError: (error: any) => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "error",
            message: "Error rejecting bonus application",
          },
        });
        console.error("Error rejecting bonus application:", error);
      },
    });
  };

  const handleSaveAsDraft = (
    id: number,
    amount: number | null,
    bonusSheetJson: string,
    exEmployee: string | null,
    comments: string[] = []
  ) => {
    if (!id) return;

    const editData: EditBonusApplicationPayload = {
      BonusApplicationId: id,
      UserAction: "Draft",
      Amount: amount || 0,
      BonusSheet: bonusSheetJson,
      ExEmployee: exEmployee,
      Comments: comments,
    };

    // @ts-ignore
    editBonusMutation.mutate(editData, {
      onSuccess: () => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "success",
            message: "Bonus application saved as draft successfully",
          },
        });
        navigate("/review-items");
      },
      onError: (error) => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "error",
            message: "Error saving bonus application",
          },
        });
        console.error("Error saving bonus application:", error);
      },
    });
  };

  const handleSubmit = (
    id: number,
    amount: number | null,
    bonusSheetJson: string,
    exEmployee: string | null,
    isConfirmed: boolean = false,
    comments: string[] = [],
    hasOverriddenHierarchy: boolean = false,
    formValidationErrors: string[] = [],
    submitterName: string | null = null,
    isOverrideConfirmed: boolean = false
  ) => {
    if (!id) return;

    setErrorMessage(null);

    // ✅ ALSO show numbered list on submit (same behavior)
    if (formValidationErrors.length > 0) {
      const count = formValidationErrors.length;

      const errorMsg = (
        <div>
          <div>
            {count} required {count === 1 ? "field is" : "fields are"} incomplete:
          </div>
          <ol
            style={{
              marginTop: 8,
              marginBottom: 0,
              paddingLeft: 24,
              listStyleType: "decimal",
            }}
          >
            {formValidationErrors.map((error, index) => (
              <li key={`${index}-${error}`}>{error}</li>
            ))}
          </ol>
        </div>
      );

      setErrorMessage(errorMsg);
      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: {
          type: "error",
          message: errorMsg,
        },
      });
      return;
    }

    if (amount === null || amount === 0 || amount < 0) {
      const errorMsg = "Invalid amount. Amount must be greater than 0.";
      setErrorMessage(errorMsg);
      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: {
          type: "error",
          message: errorMsg,
        },
      });
      return;
    }

    if (hasOverriddenHierarchy && !isOverrideConfirmed) {
      setOverrideConfirmName("");
      setPendingAction(() => () =>
        handleSubmit(id, amount, bonusSheetJson, exEmployee, isConfirmed, comments, hasOverriddenHierarchy, formValidationErrors, submitterName, true)
      );
      setIsConfirmOverrideDialogOpen(true);
      return;
    }

    if (!isConfirmed) {
      setIsConfirmSubmissionDialogOpen(true);
      return;
    }

    const editData: EditBonusApplicationPayload = {
      BonusApplicationId: id,
      UserAction: "Submitted",
      Amount: amount || 0,
      BonusSheet: bonusSheetJson,
      ExEmployee: exEmployee,
      Comments: comments,
    };

    // @ts-ignore
    editBonusMutation.mutate(editData, {
      onSuccess: () => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "success",
            message: "Bonus application submitted successfully",
          },
        });
        navigate("/history");
      },
      onError: (error) => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "error",
            message: "Error submitting bonus application",
          },
        });
        console.error("Error submitting bonus application:", error);
      },
    });
  };

  const handlePayrollProcessing = (id: number, comments: string[] = []) => {
    if (!id) return;

    const editData = {
      BonusApplicationId: id,
      UserAction: "Payroll_Processing",
      Comments: comments,
    };

    // @ts-ignore
    editBonusMutation.mutate(editData, {
      onSuccess: () => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "success",
            message: "Bonus application submitted successfully",
          },
        });
        navigate("/history");
      },
      onError: (error) => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "error",
            message: "Error submitting bonus application",
          },
        });
        console.error("Error submitting bonus application:", error);
      },
    });
  };

  const handlePaid = (id: number, comments: string[] = []) => {
    if (!id) return;

    const editData = {
      BonusApplicationId: id,
      UserAction: "Paid",
      PaymentDate: new Date().toISOString(),
      PaymentReference: "Payment Reference",
      Comments: comments,
    };

    // @ts-ignore
    editBonusMutation.mutate(editData, {
      onSuccess: () => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "success",
            message: "Bonus application submitted successfully",
          },
        });
        navigate("/history");
      },
      onError: (error) => {
        dispatch({
          type: "SHOW_NOTIFICATION",
          payload: {
            type: "error",
            message: "Error submitting bonus application",
          },
        });
        console.error("Error submitting bonus application:", error);
      },
    });
  };

  const renderActionButtons = (
    isReviewMode: boolean,
    bonusApplicationId: number | undefined,
    currentStatus: string | null | undefined,
    bonusSheetJson: string,
    projectNumbers: string | null,
    amount: number | null,
    applicationType: string,
    hasOverriddenHierarchy: boolean,
    bonusRecipientEmail: string,
    submitterEmail: string,
    submitterName: string,
    vpEmail: string,
    comments: string[] = [],
    formValidationErrors: string[] = [],
    recipientName: string | null = null,
    recipientJobTitle: string | null = null,
    recipientJobFamily: string | null = null,
    loggedInUserName: string | null = null
  ) => {
    if (!isReviewMode) return null;

    if (currentStatus === "Submitted" || currentStatus?.includes("Review")) {
      return (
        <>
          {bonusApplicationId && bonusApplicationId > 0 && (
            <button
              onClick={() => handleReject(bonusApplicationId, comments, hasOverriddenHierarchy, loggedInUserName)}
              disabled={editBonusMutation.isPending}
              className="sf-btn-reject"
            >
              Reject
            </button>
          )}
          {bonusApplicationId && bonusApplicationId > 0 && (
            <button
              onClick={() => handleApprove(bonusApplicationId, comments, hasOverriddenHierarchy, null, loggedInUserName)}
              disabled={editBonusMutation.isPending}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
            >
              Approve
            </button>
          )}
        </>
      );
    }

    if (currentStatus == null) {
      return (
        <div className="flex flex-col space-y-4">
          <div className="flex space-x-4">
            {isConfirmSubmissionDialogOpen && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="p-6 rounded shadow-lg" style={{ background: "var(--card)", color: "var(--card-foreground)" }}>
                    <h2 className="text-lg font-bold mb-4">
                      Confirm Bonus Application Submission
                    </h2>
                    <p className="mb-4">
                      Please enter your full name (matching{" "}
                      <span className="font-bold">{submitterName}</span>) to confirm
                      that the information in this form is accurate and complete.
                    </p>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          fullName.trim() === submitterName.trim() &&
                          !createBonusMutation.isPending
                        ) {
                          handleCreate(
                            projectNumbers,
                            bonusSheetJson,
                            "Submitted",
                            amount,
                            applicationType,
                            hasOverriddenHierarchy,
                            bonusRecipientEmail,
                            submitterEmail,
                            submitterName,
                            vpEmail,
                            comments,
                            true,
                            formValidationErrors
                          );
                        }
                      }}
                      className="w-full p-2 mb-4 rounded"
                      style={{ border: "1px solid var(--input)", background: "var(--background)", color: "var(--foreground)" }}
                      placeholder="Enter your full name"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={() => setIsConfirmSubmissionDialogOpen(false)}
                        className="px-4 py-2 rounded mr-2"
                        style={{ background: "var(--secondary)", color: "var(--secondary-foreground)", border: "1px solid var(--border)" }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() =>
                          handleCreate(
                            projectNumbers,
                            bonusSheetJson,
                            "Submitted",
                            amount,
                            applicationType,
                            hasOverriddenHierarchy,
                            bonusRecipientEmail,
                            submitterEmail,
                            submitterName,
                            vpEmail,
                            comments,
                            true,
                            formValidationErrors
                          )
                        }
                        className="px-4 py-2 rounded disabled:opacity-50 flex items-center gap-2"
                        style={{
                          background: fullName.trim() === submitterName.trim() && !createBonusMutation.isPending ? "var(--primary)" : "var(--muted)",
                          color: fullName.trim() === submitterName.trim() && !createBonusMutation.isPending ? "var(--primary-foreground)" : "var(--muted-foreground)",
                          cursor: fullName.trim() !== submitterName.trim() || createBonusMutation.isPending ? "not-allowed" : "pointer",
                        }}
                        disabled={fullName.trim() !== submitterName.trim() || createBonusMutation.isPending}
                      >
                        {createBonusMutation.isPending && (
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() =>
                handleCreate(
                  projectNumbers,
                  bonusSheetJson,
                  "Draft",
                  amount,
                  applicationType,
                  hasOverriddenHierarchy,
                  bonusRecipientEmail,
                  submitterEmail,
                  submitterName,
                  vpEmail,
                  comments,
                  false,
                  formValidationErrors
                )
              }
              disabled={createBonusMutation.isPending}
              className="px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ background: "var(--secondary)", color: "var(--secondary-foreground)", border: "1px solid var(--border)" }}
            >
              Save as Draft
            </button>

            <button
              onClick={() =>
                handleCreate(
                  projectNumbers,
                  bonusSheetJson,
                  "Submitted",
                  amount,
                  applicationType,
                  hasOverriddenHierarchy,
                  bonusRecipientEmail,
                  submitterEmail,
                  submitterName,
                  vpEmail,
                  comments,
                  false,
                  formValidationErrors
                )
              }
              disabled={createBonusMutation.isPending}
              className="px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
              style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
            >
              Submit
            </button>
          </div>
        </div>
      );
    }

    if (
      (currentStatus === "Draft" || currentStatus === "Rejected") &&
      bonusApplicationId &&
      bonusApplicationId > 0
    ) {
      return (
        <>
          <button
            onClick={() =>
              handleSaveAsDraft(
                bonusApplicationId,
                amount,
                bonusSheetJson,
                null,
                comments
              )
            }
            disabled={editBonusMutation.isPending}
            className="px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
            style={{ background: "var(--secondary)", color: "var(--secondary-foreground)", border: "1px solid var(--border)" }}
          >
            Save as Draft
          </button>

          {isConfirmSubmissionDialogOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="p-6 rounded shadow-lg" style={{ background: "var(--card)", color: "var(--card-foreground)" }}>
                  <h2 className="text-lg font-bold mb-4">
                    Confirm Bonus Application Submission
                  </h2>
                  <p className="mb-4">
                    Please enter your full name (matching{" "}
                    <span className="font-bold">{submitterName}</span>) to confirm
                    that the information in this form is accurate and complete.
                  </p>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        fullName.trim() === submitterName.trim() &&
                        !editBonusMutation.isPending
                      ) {
                        handleSubmit(
                          bonusApplicationId,
                          amount,
                          bonusSheetJson,
                          null,
                          true,
                          comments,
                          hasOverriddenHierarchy,
                          formValidationErrors,
                          submitterName,
                          true
                        );
                      }
                    }}
                    className="w-full p-2 mb-4 rounded"
                    style={{ border: "1px solid var(--input)", background: "var(--background)", color: "var(--foreground)" }}
                    placeholder="Enter your full name"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={() => setIsConfirmSubmissionDialogOpen(false)}
                      className="px-4 py-2 rounded mr-2"
                      style={{ background: "var(--secondary)", color: "var(--secondary-foreground)", border: "1px solid var(--border)" }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() =>
                        handleSubmit(
                          bonusApplicationId,
                          amount,
                          bonusSheetJson,
                          null,
                          true,
                          comments,
                          hasOverriddenHierarchy,
                          formValidationErrors,
                          submitterName,
                          true
                        )
                      }
                      className="px-4 py-2 rounded disabled:opacity-50 flex items-center gap-2"
                      style={{
                        background: fullName.trim() === submitterName.trim() && !editBonusMutation.isPending ? "var(--primary)" : "var(--muted)",
                        color: fullName.trim() === submitterName.trim() && !editBonusMutation.isPending ? "var(--primary-foreground)" : "var(--muted-foreground)",
                        cursor: fullName.trim() !== submitterName.trim() || editBonusMutation.isPending ? "not-allowed" : "pointer",
                      }}
                      disabled={fullName.trim() !== submitterName.trim() || editBonusMutation.isPending}
                    >
                      {editBonusMutation.isPending && (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() =>
              handleSubmit(
                bonusApplicationId,
                amount,
                bonusSheetJson,
                null,
                false,
                comments,
                hasOverriddenHierarchy,
                formValidationErrors,
                submitterName
              )
            }
            disabled={editBonusMutation.isPending}
            className="px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
            style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
          >
            Submit
          </button>
        </>
      );
    }

    // if (currentStatus === "Approved" && bonusApplicationId && bonusApplicationId > 0) {
    //   return (
    //     <button
    //       onClick={() => handlePayrollProcessing(bonusApplicationId, comments)}
    //       className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
    //     >
    //       Process Payment
    //     </button>
    //   );
    // }

    // if (
    //   currentStatus === "Payroll_Processing" &&
    //   bonusApplicationId &&
    //   bonusApplicationId > 0
    // ) {
    //   return (
    //     <button
    //       onClick={() => handlePaid(bonusApplicationId, comments)}
    //       className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
    //     >
    //       Confirm Payment
    //     </button>
    //   );
    // }

    return null;
  };

  return {
    handleCreate,
    handleSaveAsDraft,
    handleSubmit,
    handleApprove,
    handleReject,
    handlePayrollProcessing,
    handlePaid,
    renderActionButtons,
    errorMessage,
    isConfirmOverrideDialogOpen,
    overrideConfirmName,
    setOverrideConfirmName,
    pendingAction,
    setPendingAction,
    setIsConfirmOverrideDialogOpen,
    editBonusMutation,
    createBonusMutation,
  };
};

export const ActionButtons: React.FC<{
  isReviewMode: boolean;
  bonusApplicationId: number | undefined;
  currentStatus: string | null | undefined;
  bonusSheetJson: string;
  projectNumbers: string | null;
  amount: number | null;
  applicationType: string;
  hasOverriddenHierarchy: boolean;
  bonusRecipientEmail: string;
  submitterEmail: string;
  submitterName: string;
  vpEmail: string;
  comments: string[];
  formValidationErrors: string[];
  recipientName: string | null;
  recipientJobTitle: string | null;
  recipientJobFamily: string | null;
  loggedInUserName: string | null;
}> = (props) => {
  const [state]: any = useContext(UIContext);

  const actionHooks = useActionButtons();
  const {
    renderActionButtons,
    errorMessage,
    isConfirmOverrideDialogOpen,
    overrideConfirmName,
    setOverrideConfirmName,
    pendingAction,
    setPendingAction,
    setIsConfirmOverrideDialogOpen,
    editBonusMutation,
    createBonusMutation,
  } = actionHooks;

  const confirmAgainstName =
    props.currentStatus === "Submitted" || props.currentStatus?.includes("Review")
      ? props.loggedInUserName ?? ""
      : props.submitterName ?? "";

  const isPending =
    props.currentStatus === "Submitted" || props.currentStatus?.includes("Review")
      ? editBonusMutation.isPending
      : props.currentStatus == null
        ? createBonusMutation.isPending
        : editBonusMutation.isPending;

  const globalErrorMessage: ReactNode =
    state.notifications?.find((n: { type: string; message: ReactNode }) => n.type === "error")
      ?.message ?? null;

  console.log("Rendering action buttons with props:", props);

  const buttons = renderActionButtons(
    props.isReviewMode,
    props.bonusApplicationId,
    props.currentStatus,
    props.bonusSheetJson,
    props.projectNumbers,
    props.amount,
    props.applicationType,
    props.hasOverriddenHierarchy,
    props.bonusRecipientEmail,
    props.submitterEmail,
    props.submitterName,
    props.vpEmail,
    props.comments,
    props.formValidationErrors,
    props.recipientName,
    props.recipientJobTitle,
    props.recipientJobFamily,
    props.loggedInUserName
  );

  if (!buttons) return null;

  return (
    <div className="flex flex-col space-y-4">
      {isConfirmOverrideDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="p-6 rounded shadow-lg" style={{ background: "var(--card)", color: "var(--card-foreground)", maxWidth: 480, width: "100%" }}>
              <h2 className="text-lg font-bold mb-4">Confirm Override Acknowledgement</h2>

              <div style={{ background: "#fff5f5", border: "1.5px solid #f87171", borderRadius: "8px", padding: "12px 16px", fontSize: 12, color: "#991b1b", display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Override Active: Employee Hierarchy is Overridden</span>
                </div>
                <span style={{ color: "#7f1d1d", lineHeight: 1.5 }}>
                  This recipient was selected under a hierarchy override — the standard reporting hierarchy was not enforced at the time of selection.
                </span>
                {(props.recipientName || props.recipientJobTitle || props.recipientJobFamily || props.applicationType) && (
                  <div style={{ marginTop: 4, padding: "8px 10px", background: "#fee2e2", borderRadius: "6px", display: "flex", flexWrap: "wrap", gap: "4px 20px", color: "#7f1d1d" }}>
                    {props.recipientName && <span><span style={{ fontWeight: 600 }}>Recipient:</span> {props.recipientName}</span>}
                    {props.recipientJobTitle && <span><span style={{ fontWeight: 600 }}>Recipient Job Title:</span> {props.recipientJobTitle}</span>}
                    {props.recipientJobFamily && <span><span style={{ fontWeight: 600 }}>Job Family:</span> {props.recipientJobFamily}</span>}
                    {props.applicationType && <span><span style={{ fontWeight: 600 }}>Application Type:</span> {props.applicationType}</span>}
                  </div>
                )}
              </div>

              <p className="mb-4" style={{ fontSize: 13 }}>
                Please enter your full name (matching{" "}
                <span className="font-bold">{confirmAgainstName}</span>) to acknowledge
                that you have reviewed and accept the hierarchy override for this application.
              </p>
              <input
                type="text"
                value={overrideConfirmName}
                onChange={(e) => setOverrideConfirmName(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    overrideConfirmName.trim() === confirmAgainstName.trim() &&
                    !isPending &&
                    pendingAction
                  ) {
                    setIsConfirmOverrideDialogOpen(false);
                    setOverrideConfirmName("");
                    pendingAction();
                    setPendingAction(null);
                  }
                }}
                className="w-full p-2 mb-4 rounded"
                style={{ border: "1px solid var(--input)", background: "var(--background)", color: "var(--foreground)" }}
                placeholder="Enter your full name"
              />
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setIsConfirmOverrideDialogOpen(false);
                    setOverrideConfirmName("");
                    setPendingAction(null);
                  }}
                  className="px-4 py-2 rounded mr-2"
                  style={{ background: "var(--secondary)", color: "var(--secondary-foreground)", border: "1px solid var(--border)" }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (pendingAction) {
                      setIsConfirmOverrideDialogOpen(false);
                      setOverrideConfirmName("");
                      pendingAction();
                      setPendingAction(null);
                    }
                  }}
                  className="px-4 py-2 rounded disabled:opacity-50 flex items-center gap-2"
                  style={{
                    background: overrideConfirmName.trim() === confirmAgainstName.trim() && !isPending ? "var(--primary)" : "var(--muted)",
                    color: overrideConfirmName.trim() === confirmAgainstName.trim() && !isPending ? "var(--primary-foreground)" : "var(--muted-foreground)",
                    cursor: overrideConfirmName.trim() !== confirmAgainstName.trim() || isPending ? "not-allowed" : "pointer",
                  }}
                  disabled={overrideConfirmName.trim() !== confirmAgainstName.trim() || isPending}
                >
                  {isPending && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  Acknowledge & Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex space-x-4">{buttons}</div>
      {(errorMessage || globalErrorMessage) && (
        <div className="text-destructive text-sm">{errorMessage || globalErrorMessage}</div>
      )}
    </div>
  );
};